import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { preventDefaultBehavior } from 'assets/utils/dom/Events';
import { compact } from 'lodash';
import ProductEnhancementDocument from 'models/productManagement/productEnhancementDocument/ProductEnhancementDocument';
import { useRef } from 'react';
import { DealerForm } from '../Dealer.Edit';

export default function ProductEnhancementDocumentTranslation({
  productEnhancementDocument,
  languageIsoCode,
}: {
  productEnhancementDocument: ProductEnhancementDocument;
  languageIsoCode: Locale.Languages;
}) {
  const { lang } = getLocales();
  const fileInput = useRef<HTMLInputElement>();
  const { form } = useModalForm<DealerForm>();
  const foundDoc = compact(form.data.productEnhancementDealerDocuments).find(
    (d) => d.productEnhancementDocumentId === productEnhancementDocument.id && !d.isDeleted
  );
  const foundTranslation = compact(foundDoc?.translatedDocuments).find(
    (d) => d.languageIsoCode === languageIsoCode && !d.isDeleted
  );
  const docExists = languageIsoCode === Language.english ? !!foundDoc : !!foundTranslation;

  const alreadyUploadedDoc =
    languageIsoCode === Language.english
      ? compact(form.data.productEnhancementDealerDocuments)?.find(
          (d) => d.id && d.productEnhancementDocumentId === productEnhancementDocument.id
        )?.mediaFile
      : compact(foundDoc?.translatedDocuments)?.find((d) => d.id && d.documentId === foundDoc?.id)?.mediaFile;

  function handleUpload(file?: File) {
    const productEnhancementDealerDocuments = compact(form.data.productEnhancementDealerDocuments).map((d) =>
      d.productEnhancementDocumentId === productEnhancementDocument.id
        ? {
            ...d,
            isDeleted: true,
            translatedDocuments: compact(d.translatedDocuments).map((t) =>
              t.documentId === d.id && t.languageIsoCode === languageIsoCode ? { ...t, isDeleted: true } : t
            ),
          }
        : d
    );
    if (file) {
      productEnhancementDealerDocuments.push({
        productEnhancementDocumentId: productEnhancementDocument.id,
        productEnhancementDocument,
        formFile: {
          type: file.type as Utils.File.MimeType,
          dataUrl: window.URL.createObjectURL(file),
          original: file,
        },
        translatedDocuments: [],
      });
    }
    form.update({ productEnhancementDealerDocuments });
  }

  function handleUploadTranslation(documentId: number, formFile?: File) {
    const productEnhancementDealerDocuments = compact(form.data.productEnhancementDealerDocuments).map((d) => {
      if (!(d.productEnhancementDocumentId === productEnhancementDocument.id && documentId === d.id)) {
        return d;
      }
      const translatedDocuments = compact(d.translatedDocuments).map((t) =>
        t.documentId === d.id && t.languageIsoCode === languageIsoCode ? { ...t, isDeleted: true } : t
      );

      if (formFile) {
        translatedDocuments.push({
          documentId,
          documentType: 'ProductEnhancementDealerDocument',
          languageIsoCode,
          formFile,
        });
      }
      return {
        ...d,
        translatedDocuments,
      };
    });

    form.update({ productEnhancementDealerDocuments });
  }

  return (
    <>
      {docExists ? (
        <Button
          class={ThemeButtonCircle}
          media={<Icon class="fas-trash" />}
          style={{ display: 'inline-flex', color: Theme.Color.error }}
          onClick={() => {
            if (languageIsoCode === Language.english) {
              handleUpload();
            } else if (foundDoc) {
              handleUploadTranslation(foundDoc.id);
            }
          }}
          htmlElementProps={{
            title: lang.delete,
          }}
        />
      ) : (
        <>
          <input
            ref={fileInput}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              preventDefaultBehavior(event);
              if (languageIsoCode === Language.english) {
                handleUpload(event.currentTarget.files[0]);
              } else if (foundDoc) {
                handleUploadTranslation(foundDoc.id, event.currentTarget.files[0]);
              }
              event.currentTarget.value = '';
            }}
            type="file"
            accept="*"
            multiple={false}
            style={{ display: 'none' }}
          />
          <Button
            class={ThemeButtonCircle}
            style={{ display: 'inline-flex', color: Theme.Color.success }}
            media={<Icon class="fas-upload" />}
            data={productEnhancementDocument}
            disabled={languageIsoCode == Language.english ? false : !foundDoc}
            onClick={() => fileInput?.current.click()}
            htmlElementProps={{
              title: lang.upload,
            }}
          />
        </>
      )}
      <LinkButton
        media={<Icon class="fas-download" />}
        style={{ display: 'inline-flex' }}
        class={ThemeButtonCircle}
        disabled={!alreadyUploadedDoc}
        htmlElementProps={{
          title: `${lang.download} - ${alreadyUploadedDoc?.name}`,
        }}
        url={alreadyUploadedDoc?.url}
      />
    </>
  );
}
